import React from 'react';
import { FiBook, FiAward, FiUsers, FiTarget, FiCheckCircle, FiStar, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';

const CBSE = () => {
  return (
    <div className="animate-fadeIn">
      <SEO page="cbse" />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-indigo-600 pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-aos="fade-up">
              <SafeIcon icon={FiBook} className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Curriculum</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-aos="fade-up" data-aos-delay="100">
              CBSE Education
            </h1>
            
            <p className="text-xl text-blue-100 leading-relaxed" data-aos="fade-up" data-aos-delay="200">
              Empowering young minds through a world-class CBSE curriculum that blends academic excellence 
              with holistic development, from early years to middle school.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Learning Through{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Exploration & Discovery
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our CBSE curriculum is thoughtfully designed to ignite curiosity, foster innovation, and develop 
              well-rounded individuals through experiential learning and hands-on exploration.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="lg:col-span-2 space-y-8" data-aos="fade-right">
              <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-lg border border-gray-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiBook} className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Holistic Learning Journey</h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      Our comprehensive approach seamlessly blends structured academics with enriching co-curricular activities, 
                      cultural programs, and experiential learning opportunities. Every child is empowered to discover their unique 
                      talents while building strong academic foundations.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-lg border border-gray-100">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiStar} className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Creative Arts Integration</h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      <span className="font-semibold text-purple-600">Art, Music, and Dance</span> are integral to our curriculum, 
                      nurturing creative expression, emotional intelligence, and holistic personality development through active 
                      learning experiences.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6" data-aos="fade-left">
              <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100">
                <img 
                  src="/img/courses/img_15.jpeg" 
                  alt="Students engaged in interactive learning" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Interactive Learning</h3>
                  <p className="text-gray-600 leading-relaxed">Engaging activities that foster critical thinking and creativity.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Vision & Mission
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Our Vision */}
            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-gray-100 hover:shadow-lg transition-all duration-300" data-aos="fade-up">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiTarget} className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">Our Vision</h3>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg">
                To nurture responsible and ethical global leaders through excellence in education, fostering a strong 
                commitment to academic achievement, social responsibility, and environmental sustainability.
              </p>
            </div>

            {/* Our Mission */}
            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-gray-100 hover:shadow-lg transition-all duration-300" data-aos="fade-up" data-aos-delay="100">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiAward} className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">Our Mission</h3>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg">
                To create an innovative, nurturing learning environment where educators and students collaborate, inspire, 
                and grow together, promoting critical thinking, creativity, and a lifelong passion for learning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Learning &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Curriculum
              </span>
            </h2>
            <p className="text-gray-600 text-xl max-w-3xl mx-auto leading-relaxed">
              Comprehensive programs designed for every stage of your child's educational journey, from early years to middle school excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-16">
            {[
              {
                title: "Nursery Program",
                ageGroup: "2-3 Years",
                description: "Foundation building through play-based learning, sensory activities, and social-emotional development in a nurturing environment.",
                features: ["Sensory Play", "Motor Skills", "Social Skills", "Language Foundation"],
                image: "/img/courses/img_26.jpeg",
                color: "from-pink-400 to-rose-500"
              },
              {
                title: "Kindergarten Program",
                ageGroup: "3-4 Years",
                description: "Structured learning with focus on creative expression, pre-reading, pre-math, and character development through engaging activities.",
                features: ["Creative Arts", "Phonics Intro", "Number Concepts", "Character Building"],
                image: "/img/courses/img_25.jpeg",
                color: "from-blue-400 to-indigo-500"
              },
              {
                title: "Pre-K Program",
                ageGroup: "4-5 Years",
                description: "School readiness program with emphasis on academic skills, independence, and preparation for Grade 1 with STEM activities.",
                features: ["School Readiness", "STEM Basics", "Leadership Skills", "Academic Prep"],
                image: "/img/courses/img_16.jpeg",
                color: "from-emerald-400 to-teal-500"
              },
              {
                title: "Primary Program (Grade 1-5)",
                ageGroup: "6-10 Years",
                description: "Comprehensive CBSE curriculum with emphasis on literacy, numeracy, science exploration, and critical thinking skills development.",
                features: ["CBSE Framework", "Subject Mastery", "STEM Learning", "Life Skills"],
                image: "/img/courses/img_15.jpeg",
                color: "from-orange-400 to-amber-500"
              },
              {
                title: "Middle School (Grade 6-7)",
                ageGroup: "11-13 Years",
                description: "Advanced CBSE curriculum with specialized subjects, project-based activities, and preparation for secondary education.",
                features: ["Advanced Curriculum", "Critical Thinking", "Research Projects", "Career Awareness"],
                image: "/img/courses/img_12.jpeg",
                color: "from-purple-400 to-indigo-500"
              }
            ].map((course, index) => (
              <div
                key={course.title}
                className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                data-aos="fade-up"
                data-aos-delay={index * 150}
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={course.image}
                    alt={`${course.title} - Children learning in age-appropriate activities`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-200">
                    {course.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {course.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {course.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Meta Info */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiUsers} className="w-4 h-4" />
                      <span>Small Groups</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105">
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center" data-aos="fade-up">
            <a
              href="/academics"
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <span>Explore All Programs</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                CBSE
              </span> at Our School
            </h2>
            <p className="text-gray-600 text-xl max-w-3xl mx-auto leading-relaxed">
              Experience excellence in education with our comprehensive CBSE curriculum and holistic development approach.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: FiCheckCircle,
                title: 'Holistic Development',
                description: 'Comprehensive approach balancing academics with sports, arts, and life skills for well-rounded growth',
                color: 'from-blue-500 to-cyan-500'
              },
              {
                icon: FiUsers,
                title: 'Expert Faculty',
                description: 'Highly qualified and passionate educators dedicated to nurturing every child\'s potential',
                color: 'from-emerald-500 to-teal-500'
              },
              {
                icon: FiAward,
                title: 'Academic Excellence',
                description: 'Proven track record of outstanding results and student achievement year after year',
                color: 'from-purple-500 to-pink-500'
              },
              {
                icon: FiBook,
                title: 'Modern Curriculum',
                description: 'Updated CBSE syllabus integrated with cutting-edge technology and innovative teaching methods',
                color: 'from-orange-500 to-red-500'
              },
              {
                icon: FiTarget,
                title: 'Career Guidance',
                description: 'Comprehensive counseling and mentorship for higher education pathways and career planning',
                color: 'from-pink-500 to-rose-500'
              },
              {
                icon: FiStar,
                title: 'Co-curricular Activities',
                description: 'Diverse clubs, competitions, and enrichment programs that inspire creativity and leadership',
                color: 'from-indigo-500 to-purple-500'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="group bg-white rounded-3xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >
                <div className="relative z-10">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full mb-6">
            <SafeIcon icon={FiStar} className="w-5 h-5 text-white" />
            <span className="text-white font-semibold">Join Our Community</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Begin Your Child's Educational Journey?
          </h2>
          <p className="text-xl text-white/95 mb-10 leading-relaxed max-w-3xl mx-auto">
            Experience world-class CBSE education that nurtures academic excellence, character development, 
            and prepares your child for a bright future. Schedule your campus visit today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="/contact" 
              className="group inline-flex items-center justify-center space-x-3 bg-white text-blue-600 px-10 py-5 rounded-2xl font-bold hover:bg-blue-50 transform hover:scale-105 transition-all duration-200 shadow-2xl"
            >
              <SafeIcon icon={FiCheckCircle} className="w-6 h-6 group-hover:rotate-12 transition-transform duration-200" />
              <span className="text-lg">Schedule a Campus Visit</span>
            </a>
            <a 
              href="tel:+918106921133" 
              className="group inline-flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-2xl font-bold hover:bg-white/20 transform hover:scale-105 transition-all duration-200"
            >
              <span className="text-lg">📞 Call: 81069 21133</span>
            </a>
          </div>
          <p className="mt-8 text-white/80 text-sm">
            ✨ Limited seats available for Academic Year 2026-27 | Admissions Open
          </p>
        </div>
      </section>
    </div>
  );
};

export default CBSE;
